import type { PaginatedResponse } from '@/types'

/**
 * Walk a paginated list endpoint and return every row (F-FE-N114).
 *
 * WHY THIS SHAPE
 *
 * The first version of this walk was a `for` loop with an `await` in it: page
 * N+1 was only requested after page N had answered, and any page failing threw
 * the whole result away. On KIMMUN's till on 2026-09-03 that meant five round
 * trips over a link that was dropping and re-dialling; one lost request per
 * walk was enough to leave the counter with no menu for the evening
 * (audit/KIMMUN_MENU_NOT_LOADING_2026-09-03.md).
 *
 * So:
 *   - page 1 is fetched alone, because it carries `total_pages`; every other
 *     page is then requested at once. Two round trips, not N.
 *   - each page gets its own retries before the walk gives up. A dropped
 *     request costs one page a second attempt, not the whole menu.
 *   - a 4xx is never retried: that is the server answering (a role that may
 *     not read the list, a bad filter), not the link failing.
 */

type PageFetcher<T> = (page: number) => Promise<PaginatedResponse<T[]>>

const PAGE_RETRIES = 2
const PAGE_RETRY_DELAY_MS = 300
/** Backstop so a mis-reported total_pages can never fan out without bound. */
const MAX_PAGES = 1000

function isRefusal(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status
  return typeof status === 'number' && status >= 400 && status < 500
}

async function withRetry<T>(fetch: () => Promise<PaginatedResponse<T[]>>): Promise<PaginatedResponse<T[]>> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetch()
    } catch (err) {
      if (attempt >= PAGE_RETRIES || isRefusal(err)) throw err
      await new Promise((resolve) => setTimeout(resolve, PAGE_RETRY_DELAY_MS * 3 ** attempt))
    }
  }
}

function rowsOf<T>(res: PaginatedResponse<T[]>): T[] {
  return Array.isArray(res.data) ? res.data : []
}

export async function fetchAllPages<T>(fetchPage: PageFetcher<T>): Promise<T[]> {
  const first = await withRetry(() => fetchPage(1))
  const rows = rowsOf(first)
  const totalPages = Math.min(first.meta?.total_pages ?? 1, MAX_PAGES)
  if (rows.length === 0 || totalPages <= 1) return rows

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) => withRetry(() => fetchPage(i + 2)).then(rowsOf)),
  )
  return rows.concat(...rest)
}
